/**
 * A check kind, mapped to the move it belongs to and the shape of the command
 * that makes that move.
 *
 * `shell` has no entry. It is the escape hatch check and runs arbitrary script,
 * so there is no family it belongs to and nothing honest to say about it;
 * levels graded that way are answered by the two tables below instead.
 */
const MOVES = {
    branch_exists: { kind: 'branch', command: 'git switch -c <name>' },
    head_on_branch: { kind: 'branch', command: 'git switch <name>' },
    on_feature_branch: { kind: 'branch', command: 'git switch -c <name>' },
    commits_ahead: { kind: 'commit', command: 'git commit -m "<message>"' },
    last_commit_message: { kind: 'commit', command: 'git commit --amend -m "<message>"' },
    working_tree_clean: { kind: 'commit', command: 'git status' },
    commit_is_ancestor: { kind: 'history', command: 'git log --oneline --graph --all' },
    no_merge_commits: { kind: 'history', command: 'git rebase <base>' },
    release_exists: { kind: 'history', command: 'git tag -a <name> -m "<message>"' },
    file_exists: { kind: 'file', command: 'ls -la' },
    file_missing: { kind: 'file', command: 'git rm <file>' },
    file_contains: { kind: 'file', command: "grep -n '<text>' <file>" },
    file_not_contains: { kind: 'file', command: "grep -n '<text>' <file>" },
    tree_matches: { kind: 'tree', command: 'git diff <ref>' },
    remote_rejects_push: { kind: 'remote', command: 'git push' },
    http_response: { kind: 'service', command: 'curl -i <url>' },
    http_json: { kind: 'service', command: 'curl -s <url>' },
    http_header: { kind: 'service', command: 'curl -I <url>' },
    pull_request_exists: { kind: 'pr', command: 'gh pr create --fill' },
    pull_request_at_head: { kind: 'pr', command: 'gh pr status' },
    pull_request_field: { kind: 'pr', command: 'gh pr view --json <field>' },
    pull_request_checks: { kind: 'pr', command: 'gh pr checks' },
    pull_request_comments: { kind: 'pr', command: 'gh pr comment -b "<text>"' },
    pull_request_review: { kind: 'pr', command: 'gh pr review --approve' },
};
/**
 * The move a *level* is about, keyed by its `concept`.
 *
 * This table is the answer to the question the check-kind table above cannot
 * answer, and the reason it exists is a number: 479 of the roughly 860 checks in
 * the curriculum are `shell`, which has no entry up there on purpose. Act I was
 * a git course and grading it with git checks meant the check kind was a fair
 * proxy for what the learner was doing. Act II is processes, HTTP, SQL,
 * security, concurrency, ops and judgment; it is graded by running a script and
 * looking at the result, so 61 of 105 levels had no move at all under the check
 * kind alone, and several of the rest had a confidently wrong one — `git rm
 * <file>` on the level about finding a running process, because its last
 * objective clears two stale pidfiles.
 *
 * So the key is `concept`, which is per-level and unique, rather than the
 * `module` the levels are grouped under. Module would have been a third of the
 * entries, but a module is four levels wide and the four are not the same move:
 * `22 · The program` covers processes, signals, permissions and file
 * descriptors, and `ps -ef` is the right thing to say for exactly one of them.
 * `MODULE_MOVES` below is the fallback for anything this table has not been
 * taught, which is what keeps a newly authored level from silently having none.
 */
const CONCEPT_MOVES = {
    /* 01 · Your workstation */
    'shell-basics': { kind: 'terminal', command: 'pwd && ls -la' },
    'grep-find': { kind: 'terminal', command: "grep -rn '<text>' ." },
    /* 02 · First repository */
    'init-add-commit': { kind: 'commit', command: 'git add <file> && git commit -m "<message>"' },
    'branch-edit-commit': { kind: 'branch', command: 'git switch -c <name>' },
    'git-log': { kind: 'history', command: 'git log --oneline --graph --all' },
    'staging-area': { kind: 'commit', command: 'git add -p <file>' },
    /* 04-08 · Remotes, conflicts, history, rebase, recovery */
    'push-upstream': { kind: 'remote', command: 'git push -u origin <branch>' },
    'fetch-pull': { kind: 'remote', command: 'git fetch && git status' },
    'three-way-merge': { kind: 'history', command: 'git merge <branch>' },
    'revert-vs-reset': { kind: 'history', command: 'git revert <commit>' },
    reflog: { kind: 'history', command: 'git reflog' },
    rebase: { kind: 'history', command: 'git rebase <base>' },
    /* 09-10 · Pull requests and team git */
    squash: { kind: 'history', command: 'git rebase -i <base>' },
    'fixup-autosquash': { kind: 'history', command: 'git commit --fixup <commit>' },
    'code-review': { kind: 'pr', command: 'gh pr diff' },
    'commit-hygiene': { kind: 'history', command: 'git rebase -i <base>' },
    'force-with-lease': { kind: 'remote', command: 'git push --force-with-lease' },
    stash: { kind: 'commit', command: 'git stash list' },
    'cherry-pick': { kind: 'history', command: 'git cherry-pick <commit>' },
    bisect: { kind: 'history', command: 'git bisect start' },
    'full-workflow': { kind: 'branch', command: 'git switch -c <name>' },
    'fork-and-pull-request': { kind: 'pr', command: 'gh pr create --fill' },
    'branch-protection': { kind: 'remote', command: 'git push' },
    /* 11 · Working in a codebase */
    blame: { kind: 'legacy', command: 'git blame <file>' },
    'refactor-rename': { kind: 'legacy', command: "grep -rn '<name>' ." },
    'dead-code': { kind: 'legacy', command: "grep -rn '<name>' ." },
    modularise: { kind: 'legacy', command: 'wc -l <file>' },
    /* 12 · Releases */
    'semver-tags': { kind: 'history', command: 'git tag -a <name> -m "<message>"' },
    changelog: { kind: 'writing', command: 'git log --oneline <from>..<to>' },
    'release-branch': { kind: 'branch', command: 'git switch -c <name> <tag>' },
    'revert-merge': { kind: 'history', command: 'git revert -m 1 <merge>' },
    /* 13-14 · Debugging and tests */
    'failing-test': { kind: 'tests', command: 'node --test' },
    'stack-trace': { kind: 'tests', command: 'node --test 2>&1 | head -40' },
    'regression-test': { kind: 'tests', command: 'node --test' },
    reproduction: { kind: 'tests', command: 'node --test' },
    'flaky-test': { kind: 'tests', command: 'for i in $(seq 20); do node --test; done' },
    /* 15 · Production */
    'log-triage': { kind: 'observability', command: "grep -n '<text>' <logfile>" },
    'mitigate-first': { kind: 'observability', command: 'tail -n 50 <logfile>' },
    postmortem: { kind: 'writing', command: 'ls -la && cat <file>' },
    /* 16 · Automation */
    'build-script': { kind: 'automation', command: 'cat <script> && bash <script>' },
    'git-hooks': { kind: 'automation', command: 'ls -la .git/hooks/' },
    'ci-config': { kind: 'automation', command: 'cat <workflow>' },
    /* 17 · Data */
    migration: { kind: 'data', command: 'sqlite3 <db> ".schema <table>"' },
    idempotence: { kind: 'data', command: 'sqlite3 <db> "SELECT COUNT(*) FROM <table>;"' },
    'data-safety': { kind: 'data', command: 'sqlite3 <db> ".schema <table>"' },
    /* 18 · Design decisions */
    'feature-flag': { kind: 'config', command: "grep -rn '<flag>' ." },
    'api-compatibility': { kind: 'service', command: 'curl -i <url>' },
    performance: { kind: 'scale', command: 'time <command>' },
    'decision-record': { kind: 'writing', command: 'ls -la && cat <file>' },
    /* 19 · Own the service */
    'dependency-review': { kind: 'config', command: 'cat package.json' },
    runbook: { kind: 'writing', command: 'ls -la && cat <file>' },
    'end-to-end': { kind: 'tests', command: 'node --test' },
    /* 22 · The program */
    processes: { kind: 'process', command: 'ps -ef' },
    signals: { kind: 'process', command: 'kill -TERM <pid>' },
    permissions: { kind: 'process', command: 'ls -l <path>' },
    'streams-and-fds': { kind: 'process', command: '<command> > <file> 2>&1' },
    /* 23 · The network */
    'http-by-hand': { kind: 'service', command: 'curl -i <url>' },
    'status-codes': { kind: 'service', command: 'curl -i <url>' },
    timeouts: { kind: 'service', command: 'curl --max-time <seconds> -i <url>' },
    'retries-backoff': { kind: 'service', command: 'curl -i <url>' },
    /* 24 · The database */
    query: { kind: 'data', command: 'sqlite3 <db> "<query>"' },
    indexes: { kind: 'data', command: 'sqlite3 <db> "EXPLAIN QUERY PLAN <query>"' },
    'n-plus-one': { kind: 'data', command: "grep -n 'SELECT' <file>" },
    transactions: { kind: 'data', command: 'sqlite3 <db> ".schema <table>"' },
    /* 25 · The API */
    'resource-design': { kind: 'service', command: 'curl -s <url>' },
    versioning: { kind: 'service', command: 'curl -i <url>' },
    pagination: { kind: 'scale', command: 'curl -s "<url>?limit=<n>"' },
    'error-contracts': { kind: 'service', command: 'curl -i <url>' },
    /* 26 · Security + the Security track */
    'leaked-secret': { kind: 'security', command: "git log -p -S '<text>'" },
    injection: { kind: 'security', command: "grep -rn '<pattern>' ." },
    authz: { kind: 'security', command: 'curl -i -H "Authorization: <token>" <url>' },
    'vulnerable-dependency': { kind: 'security', command: 'cat package-lock.json' },
    'threat-model': { kind: 'writing', command: 'ls -la && cat <file>' },
    'output-encoding': { kind: 'security', command: "grep -rn '<pattern>' ." },
    'path-traversal': { kind: 'security', command: "grep -rn '<pattern>' ." },
    /* 27 · Concurrency */
    'race-condition': { kind: 'concurrency', command: "grep -n 'SELECT\\|UPDATE' <file>" },
    locks: { kind: 'concurrency', command: 'flock <lockfile> <command>' },
    queues: { kind: 'concurrency', command: 'ls -la <queue-dir>' },
    'at-least-once': { kind: 'concurrency', command: "grep -n '<id>' <file>" },
    /* 28 · Observability */
    metrics: { kind: 'observability', command: 'awk "{print \\$<n>}" <logfile> | sort | uniq -c' },
    tracing: { kind: 'observability', command: "grep -n '<request-id>' <logfile>" },
    alerting: { kind: 'observability', command: 'cat <rules-file>' },
    /* 29 · Scale and cost */
    caching: { kind: 'scale', command: 'time <command>' },
    load: { kind: 'scale', command: 'time <command>' },
    cost: { kind: 'scale', command: 'wc -l <logfile>' },
    /* 30 · Legacy */
    'read-unfamiliar': { kind: 'legacy', command: 'wc -l <file> && git log --oneline <file>' },
    'characterisation-tests': { kind: 'tests', command: 'node --test' },
    'strangler-fig': { kind: 'legacy', command: "grep -rn '<name>' ." },
    'big-migration': { kind: 'legacy', command: 'sqlite3 <db> ".schema <table>"' },
    /* 31 · Configuration */
    'twelve-factor': { kind: 'config', command: "grep -rn 'process.env' ." },
    'secrets-vs-config': { kind: 'config', command: "grep -rn '<pattern>' ." },
    'reproducible-build': { kind: 'config', command: 'cat package-lock.json' },
    /* 32 · The craft */
    boundaries: { kind: 'legacy', command: 'wc -l <file>' },
    'wrong-abstraction': { kind: 'legacy', command: "grep -rn '<name>' ." },
    naming: { kind: 'legacy', command: "grep -rn '<name>' ." },
    'tech-debt': { kind: 'writing', command: 'ls -la && cat <file>' },
    /* 33 · The engineer */
    estimation: { kind: 'writing', command: 'ls -la && cat <file>' },
    scoping: { kind: 'writing', command: 'ls -la && cat <file>' },
    onboarding: { kind: 'writing', command: 'ls -la && cat <file>' },
    pushback: { kind: 'writing', command: 'ls -la && cat <file>' },
    /* 34 · The whole job, and the checkpoints */
    'incident-end-to-end': { kind: 'observability', command: 'tail -n 50 <logfile>' },
    'feature-end-to-end': { kind: 'tests', command: 'node --test' },
    'own-the-system': { kind: 'tests', command: 'node --test' },
    recovery: { kind: 'history', command: 'git reflog' },
    quality: { kind: 'tests', command: 'node --test' },
    /* The AI Engineering track (docs/ai-track.md). World 1 is review of an
       agent's pull requests; worlds 2-4 are a client of the model stub, whose
       own record of what it was asked is the thing worth reading. */
    'verify-the-claim': { kind: 'pr', command: 'gh pr diff <number>' },
    'hallucinated-api': { kind: 'pr', command: "grep -rn '<name>' node_modules/<package>" },
    'unrequested-change': { kind: 'pr', command: 'git diff -w <base>...<branch>' },
    'agent-guardrails': { kind: 'automation', command: 'gh pr checks <number>' },
    'structured-output': { kind: 'service', command: 'curl -s $MODEL_BASE_URL/_stub/ledger' },
    'stop-reasons': { kind: 'service', command: 'curl -s $MODEL_BASE_URL/_stub/ledger' },
    'prompt-caching': { kind: 'scale', command: 'curl -s $MODEL_BASE_URL/_stub/usage' },
    evals: { kind: 'tests', command: 'node <eval-script>' },
    'tool-use-loop': { kind: 'service', command: 'curl -s $MODEL_BASE_URL/_stub/ledger' },
    'tool-permissions': { kind: 'security', command: "grep -rn '<tool-name>' assistant" },
    'prompt-injection': { kind: 'security', command: "grep -rn '<tool-name>' assistant" },
    'agent-budgets': { kind: 'scale', command: 'curl -s $MODEL_BASE_URL/_stub/usage' },
    retrieval: { kind: 'data', command: 'node <eval-script>' },
    grounding: { kind: 'data', command: 'node <eval-script>' },
    'model-upgrade': { kind: 'config', command: "grep -rn 'model' assistant" },
    'ai-incident': { kind: 'observability', command: 'curl -s $MODEL_BASE_URL/_stub/ledger' },
};
/**
 * The same thing at module granularity, consulted only when `CONCEPT_MOVES`
 * has nothing.
 *
 * A level authored tomorrow gets a concept nobody has written an entry for, and
 * the failure mode this table exists to remove is a level with no move at all —
 * which, for the filter, means a level whose placeholder rule is inert. Its
 * module is one of a much smaller, much slower-moving set, so a coarse but
 * domain-correct answer is always available: the new database level says "ask
 * the database" rather than saying nothing.
 *
 * Keyed on the numeric prefix (`24`) rather than the whole string, because the
 * part after the separator is a display name and display names get edited.
 */
const MODULE_MOVES = {
    '01': { kind: 'terminal', command: 'pwd && ls -la' },
    '02': { kind: 'commit', command: 'git status' },
    '04': { kind: 'remote', command: 'git fetch && git status' },
    '05': { kind: 'history', command: 'git merge <branch>' },
    '06': { kind: 'history', command: 'git log --oneline --graph --all' },
    '07': { kind: 'history', command: 'git rebase <base>' },
    '08': { kind: 'history', command: 'git reflog' },
    '09': { kind: 'pr', command: 'gh pr diff' },
    '10': { kind: 'history', command: 'git log --oneline --graph --all' },
    '11': { kind: 'legacy', command: "grep -rn '<name>' ." },
    '12': { kind: 'history', command: 'git tag -a <name> -m "<message>"' },
    '13': { kind: 'tests', command: 'node --test' },
    '14': { kind: 'tests', command: 'node --test' },
    '15': { kind: 'observability', command: "grep -n '<text>' <logfile>" },
    '16': { kind: 'automation', command: 'cat <script> && bash <script>' },
    '17': { kind: 'data', command: 'sqlite3 <db> ".schema <table>"' },
    '18': { kind: 'writing', command: 'ls -la && cat <file>' },
    '19': { kind: 'writing', command: 'ls -la && cat <file>' },
    '20': { kind: 'pr', command: 'gh pr status' },
    '21': { kind: 'pr', command: 'gh pr create --fill' },
    '22': { kind: 'process', command: 'ps -ef' },
    '23': { kind: 'service', command: 'curl -i <url>' },
    '24': { kind: 'data', command: 'sqlite3 <db> "<query>"' },
    '25': { kind: 'service', command: 'curl -i <url>' },
    '26': { kind: 'security', command: "grep -rn '<pattern>' ." },
    '27': { kind: 'concurrency', command: "grep -n '<pattern>' <file>" },
    '28': { kind: 'observability', command: "grep -n '<text>' <logfile>" },
    '29': { kind: 'scale', command: 'time <command>' },
    '30': { kind: 'legacy', command: 'wc -l <file> && git log --oneline <file>' },
    '31': { kind: 'config', command: "grep -rn 'process.env' ." },
    '32': { kind: 'legacy', command: "grep -rn '<name>' ." },
    '33': { kind: 'writing', command: 'ls -la && cat <file>' },
    '34': { kind: 'tests', command: 'node --test' },
    Security: { kind: 'security', command: "grep -rn '<pattern>' ." },
    AI: { kind: 'service', command: 'curl -s $MODEL_BASE_URL/_stub/usage' },
    Checkpoint: { kind: 'tests', command: 'node --test' },
};
/**
 * The move this level is about, before any objective is consulted.
 *
 * `module` is `"24 · The database"` or `"Security · Trust the input"`; both
 * split on the separator the authoring format uses, and the first field is the
 * key. A module string that is neither shape simply misses, and the caller
 * falls through to the check kind.
 */
function levelMove(mission) {
    const byConcept = mission.concept ? CONCEPT_MOVES[mission.concept] : undefined;
    if (byConcept)
        return byConcept;
    const head = String(mission.module ?? '').split('·')[0]?.trim() ?? '';
    if (!head)
        return null;
    return MODULE_MOVES[head] ?? null;
}
/**
 * The move a check asks for, looking inside `all_of` and `any_of`.
 *
 * A combinator is not itself a move — "all of these must be true" is not
 * something a learner does — so it answers with the first of its children that
 * is one. `all_of` conventionally lists its parts in the order they have to
 * happen, and `any_of` lists the acceptable routes best first, so in both cases
 * the first child is the one worth naming. The recursion is depth limited
 * because a mission is authored data and a cycle in it should cost a wrong
 * answer rather than the tab.
 */
function moveFor(check, params, depth = 0) {
    const known = MOVES[check];
    if (known)
        return known;
    if (depth >= 4)
        return null;
    if (check !== 'all_of' && check !== 'any_of')
        return null;
    const children = params?.checks;
    if (!Array.isArray(children))
        return null;
    for (const child of children) {
        if (typeof child?.check !== 'string')
            continue;
        const move = moveFor(child.check, child.with, depth + 1);
        if (move)
            return move;
    }
    return null;
}
/**
 * The move one level is about, or null where none of the three tables knows it.
 *
 * The level's own concept wins over its first objective's check kind, and that
 * order is not arbitrary: a check kind describes how a thing is *graded* rather
 * than what the learner is *doing*. The level about finding a running process
 * ends by clearing two stale files, so its first objective is a `file_missing`
 * and the check kind alone answers `git rm <file>` — a git command, for
 * untracked runtime state, on a level about `ps`. The concept says `processes`
 * and is right about every objective on the level at once.
 *
 * Hidden objectives are skipped in the fallback. A hidden objective is one the
 * learner is not told about on purpose, and the family of command it grades is
 * part of what they are not told.
 */
export function conceptMoveFor(mission) {
    const byLevel = levelMove(mission);
    if (byLevel)
        return byLevel;
    const objective = mission.objectives.find((o) => !o.hidden);
    if (!objective)
        return null;
    return moveFor(objective.check, objective.with);
}
//# sourceMappingURL=mentor.js.map